import {
  ElementType,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useSelector } from "react-redux";
import AutoSizer from "react-virtualized-auto-sizer";
import { VirtuosoHandle } from "react-virtuoso";

import { Popover } from "antd";
import { totalFacilityCountAtom, totalWellCountAtom } from "atoms";
import {
  EMPTY_HOVER_LEGEND,
  hoverLegendItemAtom,
  useHoverLegendWriter
} from "atoms/interaction";
import classnames from "classnames";
import { useAtomValue } from "jotai";
import _uniqBy from "lodash/uniqBy";
import { RootState } from "store/rootReducer";
import { useMapStore } from "stores/map";
import styled from "styled-components/macro";
import { removeCount } from "utils";
import { isOthersGroupLabel } from "utils/groupBy/isOthersGroupLabel";

import { LegendItemModel } from "models/LegendItem";

import { VirtualizedLegendList } from "components/base";
import {
  LegendItemData,
  LegendRowProps,
  LegendUsageType
} from "components/base/VirtualizedLegendList";
import { useScreenshotContext } from "components/screenshot/hooks/useScreenshotContext";

import { EntityKind } from "../../models/entityKind";
import LegendItem, { LegendItemLabel } from "./LegendItem";
import useLegendTitle from "./LegendTitle";

const ctrl_key = "Control";
const cmd_key = "Meta";
const escape_key = "esc";

// Strips the well count prefix, e.g. "(16) ", from a bin title.
const stripWellCount = (title: string): string => title.replace(/^\(\d+\)\s*/, "");
const shift_key = "Shift";

export interface LegendExtraRow {
  key: string;
  content: ReactNode;
}

/** Puts a chevron beside the Others row's checkbox that lists `rows` under it. */
export interface LegendOthersExpansion {
  expanded: boolean;
  // Replaces the Others row's label and count, e.g. while a search narrows it.
  label?: Maybe<LegendItemLabel>;
  // The first page is still in flight.
  loading: boolean;
  onToggle: () => void;
  rows: LegendExtraRow[];
}

type ExtraRowEntry = LegendItemData & { extraRow: ReactNode };
type LegendEntry = LegendItemModel | ExtraRowEntry;

// Extra rows go into the virtual list so they scroll and virtualize with the legend.
function withOthersRows(items: LegendItemModel[], rows: LegendExtraRow[]): LegendEntry[] {
  const othersIndex = items.findIndex((item) => isOthersGroupLabel(item.title));
  if (othersIndex === -1 || rows.length === 0) return items;
  const entries: ExtraRowEntry[] = rows.map((row) => ({
    value: row.key,
    title: "",
    color: "",
    extraRow: row.content
  }));
  return [
    ...items.slice(0, othersIndex + 1),
    ...entries,
    ...items.slice(othersIndex + 1)
  ];
}

type RenderLegendRow = (props: LegendRowProps<LegendEntry>) => JSX.Element;
const LegendRowContext = createContext<Nullable<RenderLegendRow>>(null);

// Keeps one row component type: a row type that changes with hover or parent
// renders remounts every row, dropping any click that straddles the remount.
function LegendRow(props: LegendRowProps<LegendEntry>) {
  const renderRow = useContext(LegendRowContext);
  return renderRow ? renderRow(props) : null;
}

export interface LegendComponentModel {
  allowChecked?: boolean;
  className: string;
  contextMenuContent?: ElementType;
  items: LegendItemModel[];
  itemTemplate?: (item: LegendItemModel) => JSX.Element;
  legendWidth?: number;
  legendTitleSize?: number;
  legendLabelSize?: number;
  selectedItems?: LegendItemModel[];
  showTitle?: boolean;
  showOnlyTemplate?: boolean;
  showCounts?: boolean;
  onChange?: (item) => void;
  onCheckChanged?: (checked, item: LegendItemModel) => void;
  onClickLock?: (item: LegendItemModel) => void;
  entityKind?: EntityKind;
  isScreenshotOverlayVisible: boolean;
  isWrappingText?: boolean;
  legendUsage?: LegendUsageType;
  legendItemsWrapperStyle?: React.CSSProperties;
  /**
   * Forces this kind's "none found" message. The population counts below only
   * cover the whole map; the screenshot legend sets this when its capture
   * frame contains no entities even though the map has plenty.
   */
  showNoEntitiesMessage?: boolean;
  othersExpansion?: Maybe<LegendOthersExpansion>;
}
function Legend({
  allowChecked = false,
  className = "",
  contextMenuContent = null,
  items = [],
  itemTemplate = null,
  legendWidth = 0,
  legendTitleSize = 14,
  legendLabelSize = 14,
  selectedItems = [],
  showTitle = false,
  showOnlyTemplate = false,
  showCounts = true,
  onChange = null,
  onCheckChanged = undefined,
  onClickLock = null,
  entityKind = EntityKind.Well,
  isScreenshotOverlayVisible = false,
  isWrappingText = false,
  legendUsage = "default",
  legendItemsWrapperStyle = undefined,
  showNoEntitiesMessage = false,
  othersExpansion = null
}: LegendComponentModel) {
  const setFlyToGroup = useMapStore((s) => s.setFlyToGroup);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { settings } = useScreenshotContext();

  const resolvedIsWrappingText = isScreenshotOverlayVisible
    ? entityKind === EntityKind.Facility
      ? Boolean(settings?.isWrappingTextMidstream ?? false)
      : Boolean(settings?.isWrappingTextUpstream ?? false)
    : isWrappingText;

  // state from store
  const selectedGroups = useSelector((state: RootState) =>
    entityKind == EntityKind.Well
      ? state.groupBy.selectedGroups
      : state.groupBy.selectedFacilityGroups
  );

  // Subscribe to hover state — Legend re-renders on hover changes so that
  // each row gets an up-to-date `active` prop.
  // This is cheap: Virtuoso only renders visible items (~10-30), and each
  // LegendItem is memo'd so only the 2 items whose active state actually
  // changed re-render.
  const hoverLegendItem = useAtomValue(hoverLegendItemAtom);
  const scheduleHoverWrite = useHoverLegendWriter();

  const totalWellCount = useAtomValue(totalWellCountAtom);
  const totalFacilityCount = useAtomValue(totalFacilityCountAtom);

  const showGroupsNotInFilter = useSelector((state: RootState) =>
    entityKind == EntityKind.Well
      ? state.filter.showGroupsNotInFilter
      : state.facilityFilter.showGroupsNotInFilter
  );

  const placeholderBinsEnabled = useSelector(
    (state: RootState) => state.groupBy.placeholderBinsEnabled
  );

  const title = useLegendTitle(entityKind);

  // state
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const [selected, setSelected] = useState<LegendItemModel[]>(selectedItems);
  const [isHovered, setIsHovered] = useState(false);
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const revealOthersRowsRef = useRef(false);
  const activeFocus = useSelector((state: RootState) => state.groupBy.activeFocus);

  // refs
  const isCtrlKeyDown = useRef(false);
  const isShiftKeyDown = useRef(false);
  const previousSelectedItem = useRef(null);
  const selectedGroupsLengthRef = useRef(0);

  // derived state
  const ContextMenuComponent = contextMenuContent as React.ElementType;
  const popoverContent = ContextMenuComponent ? (
    <ContextMenuComponent onSelect={() => setPopoverVisible(false)} />
  ) : (
    ""
  );
  // If legendWidth props is present, use that width, else use entire width
  const nextWidthInPx = legendWidth ? `${legendWidth}px` : "100%";

  // hotkey bindings
  useHotkeys(escape_key, () => {
    setSelectedItems([]);
  });

  useEffect(() => {
    if (selectedGroupsLengthRef.current !== selectedGroups.length) {
      //if selected groups differs (adding or removing) then force the
      //isSelected method to use only the selectedGroups selection
      const foundItems = selectedGroups
        .map((g) => items.find((si) => si.title === g))
        .filter((f) => f);

      setSelected(foundItems);
    }
    selectedGroupsLengthRef.current = selectedGroups.length;
  }, [selectedGroups, items]);

  // Bin titles carry their well count, which changes with the map view. Re-point the
  // selection at the refreshed bins so the focus bin and charts, which match titles
  // exactly, keep agreeing with the map, which ignores the count. Selected bins out
  // of view stay selected.
  useEffect(() => {
    setSelected((current) => {
      let changed = false;
      const next = current.map((item) => {
        const name = stripWellCount(item?.title ?? "");
        const match = items.find((i) => stripWellCount(i?.title ?? "") === name);
        if (!match || match.title === item?.title) return item;
        changed = true;
        return match;
      });
      return changed ? next : current;
    });
  }, [items]);

  // Restore selection when entering screenshot mode.
  // NOTE: Do NOT add `items` to deps here or add a useEffect(() => setSelected([]), [items])
  // reset — both break highlighted-wells-in-screenshot by clearing selection on re-render.
  // The stale right-click selection bug is handled in handleContextMenu instead.
  useEffect(() => {
    if (isScreenshotOverlayVisible && selectedGroups.length > 0 && items.length > 0) {
      // Strip well counts for comparison since they change with viewport
      const foundItems = selectedGroups
        .map((g) => {
          const groupName = stripWellCount(g);
          const found = items.find((si) => stripWellCount(si.title) === groupName);
          return found;
        })
        .filter((f) => f);

      if (foundItems.length > 0) {
        setSelected(foundItems);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScreenshotOverlayVisible]);

  function onClick(event, item) {
    if (event.detail === 1) {
      // Delay the single-click action so a fast second click (double-click) can
      // cancel it before React re-renders and replaces the DOM node. Without this
      // delay, the re-render resets the browser's click counter and detail===2
      // never fires reliably.
      clickTimerRef.current = setTimeout(() => {
        scheduleHoverWrite(EMPTY_HOVER_LEGEND);
        setSelectedItems(item);
      }, 200);
    } else if (event.detail === 2) {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
      setSelectedItems(item);
      setFlyToGroup(item.title);
    }
  }

  function setSelectedItems(item) {
    if (!item) return;
    if (isCtrlKeyDown.current) {
      //add to selected
      addToSelectedItem(item);
    } else if (isShiftKeyDown.current) {
      // Matched by title: a legend refresh rebuilds the items, so the anchor object goes stale.
      // Without an anchor (first click, or after Escape) only the clicked item is added;
      // a -1 anchor would otherwise select every row from the top.
      const anchorIndex = items.findIndex(
        (x) => x.title === previousSelectedItem.current?.title
      );
      const currentIndex = items.findIndex((x) => x.title === item.title);
      if (anchorIndex < 0) {
        addItemsToSelected([item]);
      } else if (currentIndex >= 0) {
        addItemsToSelected(
          items.slice(
            Math.min(anchorIndex, currentIndex),
            Math.max(anchorIndex, currentIndex) + 1
          )
        );
      }
    } else {
      //replace selected
      if (Array.isArray(item) && item.length == 0) {
        setSelected([]);
      } else {
        setSelected([item]);
      }
    }

    // Escape passes [], which must clear the anchor rather than become one.
    previousSelectedItem.current = Array.isArray(item) ? null : item;
  }

  useEffect(() => {
    onChange && onChange(selected);
  }, [onChange, selected]);

  const isSelected = useCallback(
    (item) => {
      return selected.map((x: LegendItemModel) => x?.title ?? "").includes(item.title);
    },
    [selected]
  );

  function addItemsToSelected(items) {
    const newList = [...selected, ...items];
    // By title: after a legend refresh the same bin arrives as a new object.
    const itemsToAdd = _uniqBy(newList, "title");
    setSelected(itemsToAdd);
  }

  function addToSelectedItem(item) {
    if (!item) return;
    const loc = selected.indexOf(item);
    if (loc >= 0) {
      //remove from since it already exists
      const newList = [...selected];
      newList.splice(loc, 1);
      setSelected(newList);
      return;
    }
    setSelected([...selected, item]);
  }

  useEffect(() => {
    function onKeyUp() {
      isCtrlKeyDown.current = false;
      isShiftKeyDown.current = false;
    }
    const onKeyDown = (event) => {
      if (!isCtrlKeyDown.current) {
        isCtrlKeyDown.current = event.key === ctrl_key || event.key === cmd_key;
      }
      if (!isShiftKeyDown.current) {
        isShiftKeyDown.current = event.key === shift_key;
      }
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("keyup", onKeyUp);

    return () => {
      isCtrlKeyDown.current = false;
      isShiftKeyDown.current = false;
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  const handleContextMenu = (item) => (e) => {
    e.preventDefault();

    // Replace selection with right-clicked item unless it is already part of the current
    // selection — in which case keep the full multi-selection so batch context-menu
    // actions apply to everything the user selected.
    if (!isSelected(item)) {
      setSelected([item]);
      previousSelectedItem.current = item;
    }
    setPopoverPosition({ x: e.pageX, y: e.pageY });
    setPopoverVisible(true);
  };

  const renderRow: RenderLegendRow = ({
    legend,
    useCount,
    titleStyle,
    colorPickerStyle,
    itemStyle
  }) => {
    if ("extraRow" in legend) return <>{legend.extraRow}</>;
    const expander =
      othersExpansion && isOthersGroupLabel(legend.title) ? othersExpansion : null;
    return (
      <LegendItem
        key={legend.value}
        itemStyle={itemStyle}
        titleStyle={titleStyle}
        colorPickerStyle={colorPickerStyle}
        {...(!isHovered && {
          active:
            removeCount(hoverLegendItem) === legend.value && activeFocus === entityKind
        })}
        allowChecked={allowChecked}
        item={legend}
        data-testid={`legend-item-${legend.value}`}
        showOnlyTemplate={showOnlyTemplate}
        selected={isSelected(legend)}
        template={itemTemplate}
        showCounts={useCount}
        onCheckChanged={onCheckChanged}
        onClick={(event) => onClick(event, legend)}
        onClickLock={onClickLock}
        onContextMenu={handleContextMenu(legend)}
        showGroupsNotInFilter={placeholderBinsEnabled ? showGroupsNotInFilter : false}
        isScreenshotOverlayVisible={isScreenshotOverlayVisible}
        isExpanded={expander?.expanded}
        label={expander?.label}
        onToggleExpanded={
          expander &&
          (() => {
            revealOthersRowsRef.current = !expander.expanded;
            expander.onToggle();
          })
        }
      />
    );
  };

  const entries = withOthersRows(
    items.filter((item) => item.count > 0 || showGroupsNotInFilter),
    othersExpansion?.expanded ? othersExpansion.rows : []
  );

  // Expanding the last visible row would otherwise add its rows below the fold.
  useEffect(() => {
    if (!revealOthersRowsRef.current || !othersExpansion?.expanded) return;
    if (othersExpansion.loading) return;
    revealOthersRowsRef.current = false;
    const othersIndex = entries.findIndex(
      (entry) => !("extraRow" in entry) && isOthersGroupLabel(entry.title)
    );
    if (othersIndex === -1 || othersIndex === entries.length - 1) return;
    virtuosoRef.current?.scrollIntoView({
      index: othersIndex + 1,
      calculateViewLocation: ({ itemBottom, viewportBottom }) =>
        itemBottom > viewportBottom ? { index: othersIndex, align: "start" } : null
    });
  });

  // The forced branch also requires empty rows, so a caller passing the prop
  // alongside a populated list cannot silently lose every row.
  const noWellMessage =
    entityKind === EntityKind.Well &&
    (totalWellCount === 0 || (showNoEntitiesMessage && !items.length));

  const noFacilityMessage =
    entityKind === EntityKind.Facility && totalFacilityCount === 0;
  // derive classnames
  const wrapperClassnames = classnames("legend", className);

  if (noWellMessage) {
    // Forced by the screenshot legend for an empty capture frame — an expected
    // state, not an error: neutral color, and the caller's label size so it
    // scales with the rest of the capture. The population-empty case keeps its
    // established error styling.
    return (
      <Wrapper className={wrapperClassnames}>
        <NoWellsContainer
          $fontSizePx={showNoEntitiesMessage ? legendLabelSize : undefined}
          $isExpectedState={showNoEntitiesMessage}>
          No wells were found.
        </NoWellsContainer>
      </Wrapper>
    );
  }

  // The frame-forced facility message mirrors the wells presentation — message
  // only, no title — so the two screenshot legends read consistently side by
  // side. The population-empty facility case keeps its title, as it always has.
  if (entityKind === EntityKind.Facility && showNoEntitiesMessage && !items.length) {
    return (
      <Wrapper className={wrapperClassnames}>
        <NoWellsContainer $fontSizePx={legendLabelSize} $isExpectedState>
          No facilities were found.
        </NoWellsContainer>
      </Wrapper>
    );
  }

  return (
    <Wrapper className={wrapperClassnames}>
      {showTitle && <TitleContainer labelSize={legendTitleSize}>{title}</TitleContainer>}

      {noFacilityMessage && !items.length && (
        <NoWellsContainer>No facilities were found.</NoWellsContainer>
      )}
      <Popover
        arrowPointAtCenter
        content={popoverContent}
        trigger="contextMenu"
        placement="rightTop"
        open={popoverVisible}
        onOpenChange={(v) => setPopoverVisible(v)}>
        <PopoverTrigger x={popoverPosition.x} y={popoverPosition.y} />
      </Popover>
      <LegendItemsWrapper
        style={{ width: nextWidthInPx, ...legendItemsWrapperStyle }}
        data-testid="legend-item-wrapper"
        onPointerLeave={() => {
          setIsHovered(false);
          // Clear Jotai hover state so map and chart highlights reset when
          // the cursor leaves the legend area entirely (Phase 1 migration).
          scheduleHoverWrite(EMPTY_HOVER_LEGEND);
        }}
        onPointerEnter={() => {
          setIsHovered(true);
        }}>
        <LegendRowContext.Provider value={renderRow}>
          <AutoSizer>
            {({ width, height }) => {
              return (
                <VirtualizedLegendList
                  legendUsage={legendUsage}
                  isWrappingText={resolvedIsWrappingText}
                  fontSize={legendLabelSize}
                  height={height}
                  itemRenderer={LegendRow}
                  items={entries}
                  showCounts={showCounts}
                  width={width}
                  virtuosoRef={virtuosoRef}
                />
              );
            }}
          </AutoSizer>
        </LegendRowContext.Provider>
      </LegendItemsWrapper>
    </Wrapper>
  );
}

// Removed memo, as it will not help in this case due to complex props. It will only add an overhead of comparing props before re-rendering.
export default Legend;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;

  font-family: Bai Jamjuree, "-apple-system", "BlinkMacSystemFont", "Segoe UI", "Roboto",
    "Oxygen", "Ubuntu", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;

  .react-contextmenu-wrapper {
    display: grid;
    grid-template-rows: max-content 1fr;
    user-select: none;

    .legend-title {
      margin: 0;
      background: var(--color-background-accent);
      display: block;
      text-align: center;
      padding: 5px;
    }
  }
`;

const PopoverTrigger = styled.span`
  position: fixed;
  top: ${(props) => props.y}px;
  left: ${(props) => props.x}px;
  width: 3px;
  height: 3px;
  background-color: red;
  border-radius: 100vmax;
  translate: -50% -50%;
  visibility: hidden;
`;

const LegendItemsWrapper = styled.div`
  min-height: 0;
  width: 100%;
  height: 100%;
`;

const TitleContainer = styled.span`
  min-width: 100%;
  font-weight: bold;
  padding: 6px 12px;
  text-align: left;
  font-size: ${(props) => props.labelSize / 10 + 0.2}rem;
  line-height: ${(props) => props.labelSize / 10 + 0.2}rem;
`;

const NoWellsContainer = styled.div`
  font-size: ${(props: { $fontSizePx?: number }) =>
    props.$fontSizePx ? `${props.$fontSizePx}px` : "1.4rem"};
  padding: 2px 16px 5px;
  color: ${(props: { $isExpectedState?: boolean }) =>
    props.$isExpectedState ? "#555555" : "var(--color-danger)"};
`;
